import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import closeIcon from '@images/checkout/challenger/close.svg';

import { useModalFocus } from './hooks/use_modal_focus';
import { TABLET_UP as DESKTOP, MOBILE } from './media';
import { CHALLENGER as T } from './tokens';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  padding: ${T.space[16]};
  box-sizing: border-box;
  background: rgba(4, 7, 17, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  /* Force this component to ignore or break standard sibling hierarchy layers */
  isolation: isolate;

  ${MOBILE} {
    align-items: flex-start;
    padding: ${T.space[32]} ${T.space[16]};
  }

  ${DESKTOP} {
    align-items: safe center;
  }
`;

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 420px;
  /* Funnel-Feedback 206:3271 — fixed 568px viewport; body scrolls. */
  height: 568px;
  max-height: min(568px, calc(100vh - 32px));
  max-height: min(568px, calc(100dvh - 32px));
  padding: ${T.space[24]};
  box-sizing: border-box;
  border-radius: 16px;
  background: ${T.color.bgPage};
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: ${T.space[16]};
  flex-shrink: 0;
  margin-bottom: ${T.space[24]};
`;

const Title = styled.h2`
  margin: 0;
  flex: 1;
  font-size: 20px;
  font-weight: ${FontWeight.Medium};
  line-height: 23px;
  color: ${T.color.textPrimary};
`;

/** Close Icon 24×24; vector leaf 14×14. */
const CloseButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  img {
    display: block;
    width: 14px;
    height: 14px;
  }
`;

const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[24]};
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(154, 161, 165, 0.5) transparent;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[8]};
  width: 100%;
  flex-shrink: 0;
`;

const SectionTitle = styled.h3`
  margin: 0;
  width: 100%;
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.textPrimary};
`;

const SectionBody = styled.p`
  margin: 0;
  width: 100%;
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

/** Figma 30:4067 — fee table on summarySurface; do not shrink in the scroll body. */
const FeeTable = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
  border-radius: ${T.radius.md};
  overflow: hidden;
  background: ${T.color.summarySurface};
`;

/** Shared two-column row layout (Figma 30:4068 header + 30:4069 rows). */
const FeeRowLayout = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[24]};
  width: 100%;
  box-sizing: border-box;
`;

const FeeHeader = styled(FeeRowLayout)`
  padding: ${T.space[16]} ${T.space[16]} ${T.space[12]};
`;

const FeeRow = styled.div`
  padding: 0 ${T.space[8]};
  box-sizing: border-box;
  border-top: 1px solid ${T.color.borderDefault};
`;

const FeeRowInner = styled(FeeRowLayout)`
  padding: ${T.space[16]} ${T.space[8]};
`;

const FeeCellLeft = styled.div`
  flex: 1 0 0;
  max-width: 164px;
  min-width: 0;
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
  color: ${T.color.textPrimary};
  white-space: nowrap;
`;

const FeeHeaderLeft = styled(FeeCellLeft)`
  font-size: 16px;
  line-height: 24px;
  white-space: normal;
`;

const FeeCellRight = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
  text-align: center;
  color: ${T.color.textPrimary};
  white-space: nowrap;
`;

const FeeHeaderRight = styled(FeeCellRight)`
  font-size: 16px;
  line-height: 24px;
`;

const FEE_ROWS = [
  { timeframe: 'More Than 48 Hours Before', fee: '-' },
  { timeframe: 'Less Than 48 Hours Before', fee: '$100' },
  { timeframe: 'Day Of', fee: '$200' },
] as const;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const CancellationPolicyModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useModalFocus(isOpen, dialogRef, closeButtonRef);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onOverlayClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.currentTarget === event.target) onClose();
  };

  // 2. Wrap your layout return statement in createPortal
  return createPortal(
    <Overlay onClick={onOverlayClick} role="presentation">
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenger-cancellation-policy-title"
        onClick={(event) => event.stopPropagation()}
      >
        <HeaderRow>
          <Title id="challenger-cancellation-policy-title">
            Cancellation and booking policies
          </Title>
          <CloseButton
            ref={closeButtonRef}
            type="button"
            aria-label="Close"
            onClick={onClose}
          >
            <img src={closeIcon} alt="" width={14} height={14} />
          </CloseButton>
        </HeaderRow>

        <Body>
          <Section>
            <SectionTitle>
              When will I be charged for my storage plan?
            </SectionTitle>
            <SectionBody>
              After your initial pickup is completed, you will be billed for
              your monthly storage and any additional costs associated with your
              initial pickup. You will continue to be billed monthly for your
              storage until you cancel.
            </SectionBody>
          </Section>

          <Section>
            <SectionTitle>
              Are there any deposits or holds required?
            </SectionTitle>
            <SectionBody>
              7 days before your initial pickup we place a temporary hold on
              your card to confirm the funds for your monthly storage are
              available. The hold releases automatically once the funds are
              verified. If you make a reservation within 7 days of your initial
              pickup, we place the hold immediately after you make your
              reservation.
            </SectionBody>
          </Section>

          <Section>
            <SectionTitle>
              What if I need to reschedule or cancel my pickup or delivery?
            </SectionTitle>
            <SectionBody>
              If you need to reschedule or cancel a pickup or delivery, you can
              through your Client portal. Depending on when you reschedule or
              cancel, you may incur a fee.
            </SectionBody>
          </Section>

          <FeeTable>
            <FeeHeader>
              <FeeHeaderLeft>
                Timeframe for Reschedule or Cancellation
              </FeeHeaderLeft>
              <FeeHeaderRight>Fee</FeeHeaderRight>
            </FeeHeader>
            {FEE_ROWS.map((row) => (
              <FeeRow key={row.timeframe}>
                <FeeRowInner>
                  <FeeCellLeft>{row.timeframe}</FeeCellLeft>
                  <FeeCellRight>{row.fee}</FeeCellRight>
                </FeeRowInner>
              </FeeRow>
            ))}
          </FeeTable>

          <Section>
            <SectionTitle>
              What if I want to cancel my storage plan before my commitment
              ends?
            </SectionTitle>
            <SectionBody>
              If you need to cancel your storage plan before your commitment
              ends, you will be required to pay the balance of your commitment
              while scheduling your final delivery.
            </SectionBody>
          </Section>
        </Body>
      </Dialog>
    </Overlay>,
    document.body, // <-- This forces the modal directly into the HTML body root
  );
};
